import type { ReactNode } from 'react';
import type { JobDetail } from '@nest-recruit/shared';
import { Card } from '../../../../components/Card';
import { SkeletonLines } from '../../../../components/Skeleton';
import { EmptyState, ServerErrorState } from '../../../../components/system-states/SystemState';
import { ChangeList } from '../../../requisitions/components/ChangeList';
import { useJobChanges } from '../../api/jobs-api';

/**
 * JOB-12 ⋯ Changes (ADR 0088): every change to what the requester wrote, on the hire request and then on this job, newest first,
 * each with only the lines that changed. Loads the first time it is opened; after that what is on screen stays while it refreshes.
 */
export function ChangesPanel({ detail }: { readonly detail: JobDetail }): ReactNode {
  const changes = useJobChanges(detail.id);
  if (changes.isPending) return <SkeletonLines count={5} />;
  if (changes.error) return <ServerErrorState onRetry={() => void changes.refetch()} />;
  const { hasRequest, changes: list } = changes.data;
  if (!hasRequest) {
    return <EmptyState isInset action={null} title="No hire request to compare with" body="This job was made directly, so there is nothing the requester wrote to compare it with." />;
  }
  if (list.length === 0) {
    return <EmptyState isInset action={null} title="No changes yet" body="Changes HR makes to what the requester wrote, on the request or on this job, will show here." />;
  }
  return (
    <Card title="Changes to what the requester wrote" meta={`${list.length} ${list.length === 1 ? 'change' : 'changes'}, newest first`}>
      <ChangeList changes={list} />
    </Card>
  );
}
